/**
 * Error Boundary Component - Catches JavaScript errors and provides fallback UI
 * Enhanced for 2W Admin Dashboard with retry capabilities and error reporting
 */

import React from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Icon,
  Divider,
} from '@shopify/polaris';
import {
  AlertTriangleIcon,
  RefreshIcon,
  ExternalIcon,
  BugIcon,
} from '@shopify/polaris-icons';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0,
      maxRetries: 3,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('Error Boundary caught an error:', error, errorInfo);

    // An aborted request is expected — a cancelled fetch from an unmounting page, or a
    // superseded one. Clear the error state HERE rather than in render().
    //
    // render() used to `return this.props.children` for an abort while leaving hasError true.
    // Since getDerivedStateFromError sets hasError on every throw and nothing reset it, a child
    // that threw again on that re-render put the boundary in a render loop: throw → hasError →
    // render children → throw. Resetting from the commit phase is both legal and terminating.
    if (error?.name === 'AbortError' || error?.message === 'Request was cancelled') {
      this.setState({ hasError: false, error: null, errorInfo: null });
      return;
    }

    this.setState({
      error,
      errorInfo,
    });

    // Report error to monitoring service (if available)
    if (window.reportError && typeof window.reportError === 'function') {
      window.reportError(error, {
        component: this.props.fallbackComponent || 'Unknown',
        errorInfo,
        userId: null,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    }
  }

  handleRetry = () => {
    const { retryCount, maxRetries } = this.state;
    
    if (retryCount < maxRetries) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: retryCount + 1,
      });
    } else {
      // Max retries reached, reload the page
      window.location.reload();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, retryCount, maxRetries } = this.state;
      const { 
        title = "Something went wrong",
        showDetails = true,
        showRetry = true,
        showReload = true,
        showHome = true,
        customActions,
        level = 'error' // 'error', 'warning', 'info'
      } = this.props;

      const canRetry = retryCount < maxRetries;
      const isNetworkError = error?.name === 'TypeError' && error?.message?.includes('fetch');
      // Aborts are handled in componentDidCatch, which resets state instead of rendering
      // children under a hasError that nothing clears. See the note there.

      return (
        <Page title="Error" fullWidth>
          <Layout>
            <Layout.Section>
              <Card>
                <Box padding="600">
                  <BlockStack gap="500">
                    <InlineStack align="center" gap="300">
                      <Icon 
                        source={isNetworkError ? RefreshIcon : level === 'warning' ? AlertTriangleIcon : BugIcon}
                        tone={level === 'warning' ? 'warning' : 'critical'}
                      />
                      <Text variant="headingLg" as="h1">
                        {isNetworkError ? 'Connection Issue' : title}
                      </Text>
                    </InlineStack>

                    <Text variant="bodyMd" tone="subdued">
                      {isNetworkError 
                        ? 'Unable to connect to the server. Please check your internet connection and try again.'
                        : 'An unexpected error occurred while loading this page. Our team has been notified.'
                      }
                    </Text>

                    {retryCount > 0 && (
                      <Banner tone="info">
                        <Text variant="bodySm">
                          Retry attempt {retryCount} of {maxRetries}
                        </Text>
                      </Banner>
                    )}

                    {/* The error MESSAGE, always. Not gated on showDetails or on NODE_ENV.

                        This screen used to say "contact support with the error details above"
                        while showing none: APIErrorBoundary passes showDetails={false}, and the
                        block below is additionally gated on NODE_ENV === 'development', which
                        Vite replaces with "production" at build time. So the one line that says
                        what actually broke was unreachable in dev AND in the build, and every
                        diagnosis had to go via the browser console.

                        This is an internal admin console, not a storefront — there is no user to
                        protect from seeing a stack-free error string, and hiding it costs a
                        round trip every time. The stack stays dev-only below. */}
                    {error && (
                      <Banner tone="critical">
                        <Text variant="bodySm" fontFamily="monospace">
                          {error.message || error.toString()}
                        </Text>
                      </Banner>
                    )}

                    <Divider />

                    {/* Action Buttons */}
                    <InlineStack gap="300" align="center">
                      {showRetry && canRetry && (
                        <Button
                          variant="primary"
                          onClick={this.handleRetry}
                          icon={RefreshIcon}
                        >
                          Try Again
                        </Button>
                      )}

                      {showReload && (!canRetry || !showRetry) && (
                        <Button
                          variant="primary"
                          onClick={this.handleReload}
                          icon={RefreshIcon}
                        >
                          Reload Page
                        </Button>
                      )}

                      {showHome && (
                        <Button
                          onClick={this.handleGoHome}
                        >
                          Go to Dashboard
                        </Button>
                      )}

                      {customActions && customActions.map((action, index) => (
                        <Button
                          key={index}
                          variant={action.variant || 'secondary'}
                          onClick={action.onClick}
                          icon={action.icon}
                          external={action.external}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </InlineStack>

                    {/* Error Details (for development/debugging) */}
                    {showDetails && process.env.NODE_ENV === 'development' && error && (
                      <>
                        <Divider />
                        <BlockStack gap="300">
                          <Text variant="headingSm" as="h3">
                            Technical Details
                          </Text>
                          <Card background="bg-surface-caution-subdued">
                            <Box padding="400">
                              <BlockStack gap="200">
                                <Text variant="bodySm" fontFamily="monospace">
                                  <strong>Error:</strong> {error.toString()}
                                </Text>
                                {error.stack && (
                                  <Text variant="bodySm" fontFamily="monospace">
                                    <strong>Stack:</strong>
                                    <br />
                                    {error.stack.split('\n').slice(0, 5).join('\n')}
                                  </Text>
                                )}
                                {errorInfo?.componentStack && (
                                  <Text variant="bodySm" fontFamily="monospace">
                                    <strong>Component Stack:</strong>
                                    <br />
                                    {errorInfo.componentStack.split('\n').slice(0, 3).join('\n')}
                                  </Text>
                                )}
                              </BlockStack>
                            </Box>
                          </Card>
                        </BlockStack>
                      </>
                    )}

                    {/* Help Text */}
                    <Box paddingBlockStart="400">
                      <Text variant="bodySm" tone="subdued" alignment="center">
                        If this problem persists, please contact support with the error details above.
                      </Text>
                    </Box>
                  </BlockStack>
                </Box>
              </Card>
            </Layout.Section>
          </Layout>
        </Page>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap components with error boundary
 */
export const withErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
  const WithErrorBoundaryComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  
  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;
  
  return WithErrorBoundaryComponent;
};

/**
 * Hook for handling async errors in function components
 */
export const useErrorHandler = () => {
  const [, setError] = React.useState();
  
  const handleError = React.useCallback((error) => {
    // Report error and trigger error boundary
    console.error('Async error caught:', error);
    setError(() => {
      throw error;
    });
  }, []);
  
  return handleError;
};

/**
 * Specialized error boundary for API errors
 */
export const APIErrorBoundary = ({ children, onRetry }) => (
  <ErrorBoundary
    title="Failed to load data"
    showDetails={false}
    showRetry={true}
    customActions={onRetry ? [{
      label: 'Refresh Data',
      onClick: onRetry,
      variant: 'primary',
      icon: RefreshIcon,
    }] : undefined}
  >
    {children}
  </ErrorBoundary>
);

export default ErrorBoundary;